import { useFragment } from '@apollo/client';
import { Button } from '@synoptic/ui-kit/button/button.js';
import { FileInput } from '@synoptic/ui-kit/file-input/file-input.js';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { LexicalEditor } from 'lexical';
import { useRef, useState } from 'react';
import {
  Controller,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { FieldType, S3ObjectInput } from '@/__generated__/graphql.ts';
import {
  formImgSection,
  newPostActions,
  newPostActionsSubmitButton,
  newPostContent,
  newPostContentContainer,
  newPostForm,
} from '@/components/new-post-modal/form.css.ts';
import { TournamentLabel } from '@/components/post-label/tournament-label.tsx';
import { useUserUploads } from '@/components/s3/user/use-uploads.ts';
import { waitForUploads } from '@/components/s3/wait-for-uploads.ts';
import { TextEditor } from '@/components/text-editor/text-editor.tsx';
import { stateToText } from '@/components/text-editor/utils/state-to-text.ts';
import { UploadPreview } from '@/components/upload-preview/upload-preview.tsx';
import { TournamentPostFormFragment } from './new-tournament-post-form-fragment.ts';
import { useAddTournamentPost } from './use-add-tournament-post.ts';

const defaultRenderHeader = (title: string) => <h1>{title}</h1>;

export type NewTournamentPostFormProps = {
  tournamentId: string;
  onSubmit: () => void;
  renderHeader?: (title: string) => React.ReactNode;
};
export const NewTournamentPostForm: React.FC<NewTournamentPostFormProps> = ({
  tournamentId,
  onSubmit,
  renderHeader = defaultRenderHeader,
}) => {
  const { handleSubmit, formState, control } = useForm();
  const toast = useToast();
  const {
    uploadFileList,
    uploads,
    onRemove: onRemoveUpload,
  } = useUserUploads({ limit: 4, mode: 'append' });

  const [addTournamentPost] = useAddTournamentPost(tournamentId);

  const [isDragOver, setIsDragOver] = useState(false);

  const { data, complete } = useFragment({
    fragment: TournamentPostFormFragment,
    fragmentName: 'TournamentPostFormFragment',
    from: {
      __typename: 'Tournament',
      id: tournamentId,
    },
  });

  const formRef = useRef<HTMLFormElement>(null);
  const editorRef = useRef<LexicalEditor | null>(null);

  if (!complete) return null;

  const mediaField = data.fields.find(
    (field) => field.type === FieldType.Media,
  );

  const textField = data.fields.find(
    (field) => field.type === FieldType.RichText,
  );

  if (!textField) {
    throw new Error('Forms with no text field are not supported yet.');
  }

  const submit: SubmitHandler<FieldValues> = async (data) => {
    if (!editorRef.current) return;

    let media: S3ObjectInput[] = [];

    if (mediaField) {
      try {
        media = await waitForUploads(uploads);
      } catch {
        toast.error({
          title: 'Some of your media failed to upload.',
        });
        return;
      }
    }

    const { errors } = await addTournamentPost({
      fields: [
        {
          name: textField.name,
          value: data[textField.name],
        },
        ...(media?.length > 0 && mediaField
          ? [
              {
                name: mediaField.name,
                value: JSON.stringify(media),
              },
            ]
          : []),
      ],
      tournamentId,
    });

    if (!errors) {
      return onSubmit();
    }
  };

  return (
    <form
      ref={formRef}
      className={newPostForm}
      // eslint-disable-next-line react-hooks/refs
      onSubmit={handleSubmit(submit)}
      data-drag-over={isDragOver ? '' : undefined}
      onKeyDownCapture={(e) => {
        if (formRef.current && e.key === 'Enter' && e.metaKey) {
          e.stopPropagation();

          formRef.current.requestSubmit();
        }
      }}
      onDragOver={(e) => {
        if (mediaField && e.dataTransfer.items.length > 0) {
          e.preventDefault();
          setIsDragOver(true);
        }
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        if (mediaField && e.dataTransfer.files.length > 0) {
          e.preventDefault();
          uploadFileList(e.dataTransfer.files);
          setIsDragOver(false);
        }
      }}
      onPaste={(e) => {
        if (mediaField && e.clipboardData.files.length > 0) {
          e.preventDefault();
          uploadFileList(e.clipboardData.files);
        }
      }}
    >
      {renderHeader('Create new post')}
      <div className={newPostContentContainer}>
        <div className={newPostContent}>
          <Controller
            name={textField.name}
            control={control}
            rules={{
              validate: (v) =>
                Boolean(
                  v &&
                  editorRef.current &&
                  stateToText(editorRef.current.getEditorState()).trim()
                    .length > 0,
                ),
            }}
            render={({ field }) => (
              <TextEditor
                rich
                namespace="new-post"
                editorRef={(el) => {
                  field.ref(el);
                  editorRef.current = el;
                }}
                onBlur={field.onBlur}
                autoFocus
                editable
                placeholder="What are your thoughts?"
                onChange={(state) => {
                  field.onChange(JSON.stringify(state));
                }}
              />
            )}
          />

          <TournamentLabel label={data.label} tournamentId={tournamentId} />

          {uploads.length > 0 ? (
            <div className={formImgSection}>
              {uploads.map(({ key, file, progress, state }) => (
                <UploadPreview
                  key={key}
                  file={file}
                  state={state}
                  progress={progress}
                  onRemove={() => onRemoveUpload(key)}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className={newPostActions}>
        {mediaField ? (
          <FileInput
            onChange={(e) => {
              if (e.target.files) {
                uploadFileList(e.target.files);
                e.target.value = '';
              }
            }}
            accept="image/*,video/*"
            multiple
            rounded={false}
            size="medium"
          >
            Attach media
          </FileInput>
        ) : (
          <div />
        )}

        <Button
          disabled={!formState.isValid}
          loading={formState.isSubmitting}
          type="submit"
          size={'medium'}
          className={newPostActionsSubmitButton}
        >
          Post
        </Button>
      </div>
    </form>
  );
};
